import React, { useEffect, useState, useMemo } from "react";
import { Plus, Clock, Info, Search } from "lucide-react";
import api from "../lib/api";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
} from "./ui/responsive-dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";

// Service image mapping from NewHomePage.jsx
const SERVICE_IMAGES = {
  "Deep Tissue Massage":
    "https://images.unsplash.com/photo-1741934023052-26baf5535088?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170",
  "Swedish Massage":
    "https://images.unsplash.com/photo-1519824145371-296894a0daa9?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170",
  "Sports Massage":
    "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=600&auto=format&fit=crop&q=80",
  "Hot Stone Massage":
    "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=600&auto=format&fit=crop&q=80",
  "Aromatherapy Massage":
    "https://images.unsplash.com/photo-1556760544-74068565f05c?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170",
  Reflexology:
    "https://images.pexels.com/photos/5793920/pexels-photo-5793920.jpeg?_gl=1*1m9mroc*_ga*NDY3MDU0MDA1LjE3NjIyMzk5Mzk.*_ga_8JE65Q40S6*czE3NjIyMzk5MzkkbzEkZzEkdDE3NjIyMzk5NjQkajM1JGwwJGgw",
  "Thai Massage":
    "https://images.pexels.com/photos/275768/pexels-photo-275768.jpeg?_gl=1*19ka01o*_ga*NDY3MDU0MDA1LjE3NjIyMzk5Mzk.*_ga_8JE65Q40S6*czE3NjIyMzk5MzkkbzEkZzEkdDE3NjIyNDA1NDUkajI2JGwwJGgw",
  "Prenatal Massage":
    "https://images.unsplash.com/photo-1515377905703-c4788e51af15?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170",
  "Postnatal Massage":
    "https://images.unsplash.com/photo-1515377905703-c4788e51af15?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170",
};

// Helper function to get service image
function getServiceImage(serviceName) {
  return (
    SERVICE_IMAGES[serviceName] ||
    "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&auto=format&fit=crop&q=80"
  );
}

// Helper function to format category name for display
function formatCategoryName(category) {
  if (!category) return "";
  // Convert underscores to spaces and capitalize each word
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * DoorDash-style service picker
 * - Grid layout with service cards
 * - Click to open modal for duration and add-ons selection
 * - Clean, minimal design
 */

export default function ServicePickerPremium({
  selectedServiceId,
  selectedDuration,
  selectedAddOns = [],
  onServiceSelect,
  onDurationSelect,
  onAddOnsChange,
}) {
  const [services, setServices] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [tempDuration, setTempDuration] = useState(null);
  const [tempAddOns, setTempAddOns] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadPricingData();
  }, []);

  // Initialize temp selections when modal opens
  useEffect(() => {
    if (modalOpen && selectedService) {
      // Check if selectedDuration belongs to this service
      const serviceId =
        typeof selectedServiceId === "object"
          ? selectedServiceId?._id
          : selectedServiceId;
      const isSameService = serviceId === selectedService._id;

      // If same service and has selected duration, use it; otherwise auto-select first duration
      const durationToSet =
        isSameService && selectedDuration
          ? selectedDuration
          : selectedService.durations?.[0] || null;

      setTempDuration(durationToSet);
      setTempAddOns(isSameService ? [...(selectedAddOns || [])] : []);
    }
  }, [
    modalOpen,
    selectedService,
    selectedDuration,
    selectedAddOns,
    selectedServiceId,
  ]);

  async function loadPricingData() {
    try {
      setLoading(true);
      const [servicesData, addOnsData] = await Promise.all([
        api.pricing.getServices(),
        api.pricing.getAddOns(),
      ]);

      console.log("✅ Services loaded:", servicesData?.services?.length || 0);
      console.log("✅ Add-ons loaded:", addOnsData?.addOns?.length || 0);

      setServices(
        Array.isArray(servicesData?.services) ? servicesData.services : [],
      );
      setAddOns(Array.isArray(addOnsData?.addOns) ? addOnsData.addOns : []);
    } catch (error) {
      console.error("❌ Failed to load pricing:", error);
      setServices([]);
      setAddOns([]);
    } finally {
      setLoading(false);
    }
  }

  function handleServiceClick(service) {
    setSelectedService(service);
    setModalOpen(true);
    // The useEffect will handle setting tempDuration and tempAddOns
  }

  // Helper function to check if an add-on is applicable to a given duration
  // applicableTo is an array of strings: ["all"] or ["60", "75", "90"] or ["all", "60"]
  function isAddOnApplicable(addOn, durationMinutes) {
    if (!addOn?.applicableTo || !Array.isArray(addOn.applicableTo)) {
      return false;
    }

    // If "all" is in the array, it applies to all durations
    if (addOn.applicableTo.includes("all")) {
      return true;
    }

    // If no duration is specified, consider it applicable (for when no duration is selected)
    if (!durationMinutes) {
      return true;
    }

    // Check if the duration string (e.g., "60", "75", "90") is in the array
    const durStr = String(durationMinutes);
    return addOn.applicableTo.includes(durStr);
  }

  function handleDurationSelect(duration) {
    setTempDuration(duration);
    // Filter add-ons when duration changes - remove add-ons that don't apply to this duration
    const durStr = duration.duration;
    setTempAddOns((prev) =>
      prev.filter((addOn) => isAddOnApplicable(addOn, durStr)),
    );
  }

  function toggleAddOn(addOn) {
    // Check if add-on is applicable to selected duration (if duration is selected)
    if (tempDuration) {
      if (!isAddOnApplicable(addOn, tempDuration.duration)) {
        return; // Don't allow selection if not applicable to current duration
      }
    }
    // If no duration selected, allow selection (will be filtered when duration is selected)

    const isSelected = tempAddOns.some((a) => a?._id === addOn._id);
    if (isSelected) {
      setTempAddOns(tempAddOns.filter((a) => a?._id !== addOn._id));
    } else {
      setTempAddOns([...tempAddOns, addOn]);
    }
  }

  function handleConfirmSelection() {
    if (!selectedService || !tempDuration) return;

    // Update parent component
    onServiceSelect?.(selectedService);
    onDurationSelect?.(tempDuration);
    onAddOnsChange?.(tempAddOns);

    // Close modal
    setModalOpen(false);
    setSelectedService(null);
    setTempDuration(null);
    setTempAddOns([]);
  }

  function handleModalClose() {
    setModalOpen(false);
    setSelectedService(null);
    setTempDuration(null);
    setTempAddOns([]);
  }

  // Get selected service object
  const selectedServiceObj = React.useMemo(() => {
    if (!selectedServiceId) return null;
    const serviceId =
      typeof selectedServiceId === "object"
        ? selectedServiceId._id
        : selectedServiceId;

    // First try to match by MongoDB ID
    let matchedService = services.find((s) => s._id === serviceId);

    // If not found, try matching by name using hardcoded service ID mappings
    if (!matchedService && serviceId) {
      const serviceNameMappings = {
        deep_tissue: "Deep Tissue Massage",
        relaxation: "Swedish Massage",
        sports: "Sports Massage",
        hot_stone: "Hot Stone Massage",
        prenatal: "Prenatal Massage",
        aromatherapy: "Aromatherapy Massage",
        thai: "Thai Massage",
        reflexology: "Reflexology",
        couples: "Couples Massage",
        chair: "Chair Massage",
      };
      const mappedName = serviceNameMappings[serviceId];
      if (mappedName) {
        matchedService = services.find((s) => s.name === mappedName);
        // If we found a match by name, update the selected service ID to the real MongoDB ID
        if (matchedService && onServiceSelect) {
          setTimeout(() => onServiceSelect(matchedService._id), 0);
        }
      }
    }

    return matchedService || null;
  }, [selectedServiceId, services, onServiceSelect]);

  // Calculate total price
  const totalPrice =
    (tempDuration?.price || 0) +
    tempAddOns.reduce((sum, addOn) => sum + (addOn?.price || 0), 0);

  // Extract unique categories from services
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    services.forEach((service) => {
      if (service.category) {
        uniqueCategories.add(service.category);
      }
    });
    return Array.from(uniqueCategories).sort();
  }, [services]);

  // Filter services based on selected category and search query
  const filteredServices = useMemo(() => {
    let filtered = services;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (service) => service.category === selectedCategory,
      );
    }

    // Filter by search query (case-insensitive search in name and description)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((service) => {
        const nameMatch = service.name?.toLowerCase().includes(query);
        const descriptionMatch = service.description
          ?.toLowerCase()
          .includes(query);
        return nameMatch || descriptionMatch;
      });
    }

    return filtered;
  }, [services, selectedCategory, searchQuery]);

  // Filter add-ons based on selected duration
  const availableAddOns = React.useMemo(() => {
    if (!tempDuration) return []; // Don't show add-ons until duration is selected
    return addOns.filter((addOn) =>
      isAddOnApplicable(addOn, tempDuration.duration),
    );
  }, [tempDuration, addOns]);

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent" />
        <p className="mt-4 text-gray-600">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="text-center space-y-2 px-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Select Your Service
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Choose a massage type</p>
      </div>

      {/* Search and Filter Section */}
      <div className="px-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for a massage service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="w-full sm:w-64 flex-shrink-0">
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {formatCategoryName(category)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* No Results Message */}
      {filteredServices.length === 0 && !loading && (
        <div className="px-4 text-center py-12">
          <p className="text-gray-600 text-lg">
            No services found matching your search.
          </p>
          {(searchQuery || selectedCategory !== "all") && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("all");
              }}
              className="mt-4">
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Services Grid - DoorDash Style */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 px-4">
        {filteredServices.map((service) => {
          const durations = service.durations || [];
          const minPrice = Math.min(...durations.map((d) => d.price));
          const maxPrice = Math.max(...durations.map((d) => d.price));
          const serviceId =
            typeof selectedServiceId === "object"
              ? selectedServiceId?._id
              : selectedServiceId;

          // Match by ID first, then by name mapping
          let isSelected = serviceId === service._id;

          // If not matched by ID, try matching by name using hardcoded service ID mappings
          if (!isSelected && serviceId) {
            const serviceNameMappings = {
              deep_tissue: "Deep Tissue Massage",
              relaxation: "Swedish Massage",
              sports: "Sports Massage",
              hot_stone: "Hot Stone Massage",
              prenatal: "Prenatal Massage",
              aromatherapy: "Aromatherapy Massage",
              thai: "Thai Massage",
              reflexology: "Reflexology",
              couples: "Couples Massage",
              chair: "Chair Massage",
            };
            const mappedName = serviceNameMappings[serviceId];
            if (mappedName && service.name === mappedName) {
              isSelected = true;
            }
          }

          return (
            <ServiceCard
              key={service._id}
              service={service}
              minPrice={minPrice}
              maxPrice={maxPrice}
              isSelected={isSelected}
              onClick={() => handleServiceClick(service)}
            />
          );
        })}
      </div>

      {/* Selection Summary */}
      {selectedServiceObj && selectedDuration && (
        <div className="px-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-emerald-900">
                  {selectedServiceObj.name} • {selectedDuration.duration} min
                </p>
                {selectedAddOns?.length > 0 && (
                  <p className="text-xs text-emerald-700 mt-1">
                    +{selectedAddOns.length} add-on
                    {selectedAddOns.length > 1 ? "s" : ""}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-emerald-900">
                  $
                  {(selectedDuration.price || 0) +
                    (selectedAddOns || []).reduce(
                      (sum, a) => sum + (a?.price || 0),
                      0,
                    )}{" "}
                  CAD
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Selection Modal */}
      <Dialog open={modalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {selectedService.name}
                </DialogTitle>
                {selectedService.description && (
                  <DialogDescription className="text-base pt-2">
                    {selectedService.description}
                  </DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Duration Selection */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Select Duration
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {selectedService.durations?.map((dur) => {
                      const isSelected =
                        tempDuration?.duration === dur.duration;
                      return (
                        <button
                          key={dur.duration}
                          onClick={() => handleDurationSelect(dur)}
                          className={cn(
                            "p-4 rounded-lg border-2 transition-all text-center",
                            isSelected
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
                          )}>
                          <div className="text-lg font-bold text-gray-900">
                            {dur.duration} min
                          </div>
                          <div className="text-sm font-semibold text-emerald-600 mt-1">
                            ${dur.price}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Add-ons Section - Only show after duration is selected */}
                {tempDuration && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Info className="w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Add Enhancements
                      </h3>
                      <span className="text-sm text-gray-500">(Optional)</span>
                    </div>
                    {availableAddOns.length > 0 ? (
                      <div className="space-y-3">
                        {availableAddOns.map((addOn) => {
                          const isSelected = tempAddOns.some(
                            (a) => a?._id === addOn._id,
                          );
                          // Check if add-on is applicable to selected duration
                          const isApplicable = isAddOnApplicable(
                            addOn,
                            tempDuration.duration,
                          );

                          return (
                            <AddOnItem
                              key={addOn._id}
                              addOn={addOn}
                              selected={isSelected}
                              disabled={!isApplicable}
                              onToggle={() => toggleAddOn(addOn)}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Info className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>No add-ons available for this duration</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Total Price */}
                {tempDuration && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        ${totalPrice} CAD
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleModalClose}
                  className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSelection}
                  disabled={!tempDuration}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Add to Booking
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ServiceCard({ service, minPrice, maxPrice, isSelected, onClick }) {
  const [imageError, setImageError] = React.useState(false);
  const serviceImage = getServiceImage(service.name);

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative bg-white rounded-lg border-2 transition-all overflow-hidden text-left group flex flex-col-reverse sm:flex-row",
        isSelected
          ? "border-emerald-500 shadow-md"
          : "border-gray-200 hover:border-gray-300 hover:shadow-sm",
      )}>
      {/* Content Section - Left Side (2/3 width) */}
      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 line-clamp-2">
              {service.name}
            </h3>
          </div>
          {service.category && (
            <div className="mb-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                {formatCategoryName(service.category)}
              </span>
            </div>
          )}
          {service.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {service.description}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 mb-1">Starting from</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              ${minPrice}
              {minPrice !== maxPrice && (
                <span className="text-sm text-gray-500 font-normal">
                  {" "}
                  - ${maxPrice}
                </span>
              )}
              <span className="text-sm font-normal text-gray-500">+</span>
            </p>
          </div>
          {isSelected && (
            <div className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
              Selected
            </div>
          )}
        </div>
      </div>

      {/* Image Section - Right Side (1/3 width) */}
      <div className="w-full sm:w-40 md:w-48 flex-shrink-0 relative overflow-hidden bg-gray-100">
        {imageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-100">
            <div className="text-6xl sm:text-7xl opacity-20">💆</div>
          </div>
        ) : (
          <img
            src={serviceImage}
            alt={service.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        )}
        {/* Plus Button Overlay */}
        <div className="absolute bottom-3 right-3 z-10">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
            <Plus className="w-5 h-5 text-gray-900" />
          </div>
        </div>
      </div>
    </button>
  );
}

function AddOnItem({ addOn, selected, disabled = false, onToggle }) {
  return (
    <div
      className={cn(
        "flex items-start gap-4 p-4 rounded-lg border-2 transition-all",
        disabled
          ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
          : "cursor-pointer",
        !disabled &&
          (selected
            ? "border-emerald-500 bg-emerald-50"
            : "border-gray-200 bg-white hover:border-gray-300"),
      )}
      onClick={disabled ? undefined : onToggle}>
      <div className="flex-shrink-0">
        <Checkbox
          checked={selected}
          disabled={disabled}
          readOnly
          className="mt-0.5 pointer-events-none"
        />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 mb-1">{addOn.name}</h4>
        {addOn.description && (
          <p className="text-sm text-gray-600 leading-relaxed">
            {addOn.description}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-gray-900">+${addOn.price}</p>
      </div>
    </div>
  );
}
